import { Helmet } from 'react-helmet';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { withSuspenseBoundary } from '../../../components/utility/SuspenseBoundary';
import { PLAN_CREATION_REASONS } from '../../../contexts/create-plan-context/create-plan-context-types/plan-create-reason';
import { PLAN_CREATION_STEPS } from '../../../contexts/create-plan-context/create-plan-context-types/plan-creation-step';
import { CreatePlanProvider } from '../../../contexts/create-plan-context/CreatePlanProvider/CreatePlanProvider';
import { FormErrorNotificationProvider } from '../../../contexts/form-error-notification-context/FormErrorNotificationProvider';
import { prefetchMeters } from '../../../hooks/requests/use-meters/use-meters';
import { useDialog } from '../../../hooks/use-dialog';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import { CreatePlanErrorNotification } from '../../plans/CreatePlanPage/create-plan-page-components/CreatePlanErrorNotification/CreatePlanErrorNotification';
import { CreatePlanPageHeader } from '../../plans/CreatePlanPage/create-plan-page-components/CreatePlanPageHeader';
import { CreatePlanPageStepperNavigator } from '../../plans/CreatePlanPage/create-plan-page-components/CreatePlanPageStepperNavigator';
import { CreatePlanPageContent } from '../../plans/CreatePlanPage/create-plan-page-components/NewCreatePlanPageContent/CreatePlanPageContent';
import { CONTRACT_REVISE_STEPS_CTA_LABELS } from '../../plans/CreatePlanPage/create-plan-page-consts/create-plan-default-values';
import { SaveReviseCurrentDialog } from './contract-revise-page-components/SaveReviseCurrentDialog';
import {
  REVISE_CURRENT_STEPS_CONTENT,
  REVISE_FOR_RENEWAL_STEPS_CONTENT,
} from './contract-revise-page-consts/reviseStepsContent';
import { useContractRevisePagePlanCreateReason } from './contract-revise-page-hooks/use-contract-revise-page-plan-create-reason';
import { withPrefetch } from '../../../components/hocs';
import { ContractReviseForm } from './ContractReviseForm';
import { useReviseForRenewalSubmitter } from './contract-revise-page-hooks/use-revise-for-renewal-submitter';

const ContractRevisePage: React.FC = () => {
  const {
    isDialogOpen: isReviseCurrentContractDialogOpen,
    handleOpen: handleOpenReviseCurrentContractDialog,
    handleClose: handleCloseReviseCurrentContractDialog,
  } = useDialog();
  const { contractId } = useValidatedParams('contractId');

  const reviseForRenewalSubmitter = useReviseForRenewalSubmitter(contractId);

  const reviseReason = useContractRevisePagePlanCreateReason(contractId);
  const isReviseCurrentContract = reviseReason === PLAN_CREATION_REASONS.AddPhaseToCurrentContract;

  const stepsContent = isReviseCurrentContract
    ? REVISE_CURRENT_STEPS_CONTENT
    : REVISE_FOR_RENEWAL_STEPS_CONTENT;
  const defaultPlanCreationStep = isReviseCurrentContract
    ? PLAN_CREATION_STEPS.pricingAndProducts
    : PLAN_CREATION_STEPS.settings;

  return (
    <>
      <Helmet>
        <title>Revise Contract</title>
      </Helmet>
      <CreatePlanProvider
        planCreationReason={PLAN_CREATION_REASONS.AddPhaseToCurrentContract}
        defaultPlanCreationStep={defaultPlanCreationStep}
        onFinalStep={isReviseCurrentContract ? handleOpenReviseCurrentContractDialog : undefined}
        onFormSubmission={!isReviseCurrentContract ? reviseForRenewalSubmitter : undefined}
      >
        <ContractReviseForm>
          <FormErrorNotificationProvider>
            <CreatePlanPageHeader />

            <CreatePlanPageContent stepsContent={stepsContent} />

            <CreatePlanErrorNotification />

            <CreatePlanPageStepperNavigator
              stepsLabels={CONTRACT_REVISE_STEPS_CTA_LABELS}
              formClearable={false}
            />
            {isReviseCurrentContract && (
              <SaveReviseCurrentDialog
                open={isReviseCurrentContractDialogOpen}
                onClose={handleCloseReviseCurrentContractDialog}
              />
            )}
          </FormErrorNotificationProvider>
        </ContractReviseForm>
      </CreatePlanProvider>
    </>
  );
};

const ContractRevisePageWithSuspenseBoundary = withSuspenseBoundary(
  withGlobalSearchParams(withPrefetch(ContractRevisePage, prefetchMeters)),
);

export { ContractRevisePageWithSuspenseBoundary as ContractRevisePage };
